/**
 * Model-backed assistance for the flavored live-preview + LSP-ish features.
 *
 * One walk of the wire {@link ArchitectureModel} (app-shell never sees the core
 * `ResolvedModel`) builds an index that powers three things, all consistent
 * with how the description renderer resolves refs:
 *   - `resolveRef`  → pill label / validity / href.
 *   - `completions` → the `[[…]]` autocomplete list (ids, names, docs, aspects).
 *   - `hover`       → a tooltip for a `[[ref]]` under the cursor.
 *
 * The pill CLICK still routes through `resolveArchlangNav`, so navigation stays
 * authoritative; this only powers labels / suggestions / tooltips.
 */
import { slugify, classifyRef, type ClassifiedRef } from "@archlang/engine";
import type {
  ArchitectureModel,
  Interface,
  Module,
  Surface,
} from "@archlang/scene/arch-model";
import { valueToString } from "@archlang/scene/arch-model";
import type { RefResolution } from "./flavored.js";
import type { MdCompletion, MdHoverInfo } from "./lsp.js";

export interface RefResolverDoc {
  readonly name: string;
  readonly sections: readonly { readonly slug: string }[];
}

/** A node's cascaded values, keyed by dotted path (e.g. "domain",
 *  "security.zone") → rendered string. Backs the foreign getters `[[ref]]@field`
 *  (fields) and `[[ref]]@@aspect` (aspects), mirroring the core renderer. */
type ValueMap = Map<string, string>;

interface Index {
  readonly idToName: Map<string, string>;
  readonly nameType: Map<string, string>; // name → display type (first seen)
  readonly nameCount: Map<string, number>;
  readonly aspects: Set<string>;
  readonly docSlugs: Map<string, Set<string>>;
  /** id → aspect values (for `[[#id]]@@aspect`). */
  readonly aspectsById: Map<string, ValueMap>;
  /** unambiguous name → aspect values (for `[[Name]]@@aspect`; absent when the
   *  name is duplicated, matching the renderer's "single candidate" rule). */
  readonly aspectsByName: Map<string, ValueMap>;
  /** id → field values (for `[[#id]]@field`). */
  readonly fieldsById: Map<string, ValueMap>;
  /** unambiguous name → field values (for `[[Name]]@field`). */
  readonly fieldsByName: Map<string, ValueMap>;
}

/** Build a path-joined → string map of a node's set values (drops unset). */
function valueMapOf(
  entries: readonly { readonly path: readonly string[]; readonly value: unknown }[],
): ValueMap {
  const m: ValueMap = new Map();
  for (const l of entries) {
    if (l.value === undefined || l.value === null) continue;
    const s = valueToString(l.value);
    if (s !== undefined) m.set(l.path.join("."), s);
  }
  return m;
}

/** Sentinel for a name seen more than once — an ambiguous getter never
 *  resolves (matches the renderer's single-candidate rule). */
const AMBIGUOUS: ValueMap = new Map();

/** Register a node's aspect + field values under its id (always) and name
 *  (poisoned on the second sighting so ambiguous names don't resolve a getter). */
function registerLabels(
  idx: Index,
  id: string | undefined,
  name: string,
  aspects: readonly { readonly path: readonly string[]; readonly value: unknown }[] | undefined,
  fields: readonly { readonly path: readonly string[]; readonly value: unknown }[] | undefined,
): void {
  const aMap = valueMapOf(aspects ?? []);
  const fMap = valueMapOf(fields ?? []);
  if (id) {
    idx.aspectsById.set(id, aMap);
    idx.fieldsById.set(id, fMap);
  }
  idx.aspectsByName.set(name, idx.aspectsByName.has(name) ? AMBIGUOUS : aMap);
  idx.fieldsByName.set(name, idx.fieldsByName.has(name) ? AMBIGUOUS : fMap);
}

function add(m: Map<string, number>, k: string): void {
  m.set(k, (m.get(k) ?? 0) + 1);
}

function addAspects(labels: Interface["aspects"], out: Set<string>): void {
  for (const l of labels) {
    if (typeof l.value !== "string") continue;
    const key = l.path.join(".");
    out.add(key);
    out.add(`${key}:${l.value}`);
  }
}

function walkModule(m: Module, idx: Index): void {
  if (m.id) idx.idToName.set(m.id, m.name);
  add(idx.nameCount, m.name);
  if (!idx.nameType.has(m.name)) idx.nameType.set(m.name, m.typeName);
  addAspects(m.aspects, idx.aspects);
  registerLabels(idx, m.id, m.name, m.aspects, m.fields);
  for (const i of m.interfaces) {
    add(idx.nameCount, i.name);
    if (!idx.nameType.has(i.name)) idx.nameType.set(i.name, i.typeName);
    addAspects(i.aspects, idx.aspects);
    registerLabels(idx, undefined, i.name, i.aspects, i.fields);
  }
  for (const s of m.surfaces) walkSurface(s, idx);
  for (const c of m.children) walkModule(c, idx);
}

function walkSurface(s: Surface, idx: Index): void {
  add(idx.nameCount, s.name);
  registerLabels(idx, undefined, s.name, s.aspects, s.fields);
  for (const i of s.interfaces) {
    add(idx.nameCount, i.name);
    if (!idx.nameType.has(i.name)) idx.nameType.set(i.name, i.typeName);
    addAspects(i.aspects, idx.aspects);
    registerLabels(idx, undefined, i.name, i.aspects, i.fields);
  }
  for (const sub of s.surfaces) walkSurface(sub, idx);
}

function collectIndex(model: ArchitectureModel, documents: readonly RefResolverDoc[]): Index {
  const idx: Index = {
    idToName: new Map(),
    nameType: new Map(),
    nameCount: new Map(),
    aspects: new Set(),
    docSlugs: new Map(),
    aspectsById: new Map(),
    aspectsByName: new Map(),
    fieldsById: new Map(),
    fieldsByName: new Map(),
  };
  for (const m of model.modules) walkModule(m, idx);
  for (const p of model.processes) {
    if (p.id) idx.idToName.set(p.id, p.name);
    add(idx.nameCount, p.name);
    if (!idx.nameType.has(p.name)) idx.nameType.set(p.name, "process");
  }
  // Top-level subprocesses and views are resolvable refs in the authoritative
  // description renderer (buildDescriptionIndex) — keep parity here so hover /
  // completion don't flag them as unresolved.
  for (const sp of model.subprocesses ?? []) {
    if (sp.id) idx.idToName.set(sp.id, sp.name);
    add(idx.nameCount, sp.name);
    if (!idx.nameType.has(sp.name)) idx.nameType.set(sp.name, "subprocess");
  }
  for (const v of model.views ?? []) {
    if (v.id) idx.idToName.set(v.id, v.name);
    add(idx.nameCount, v.name);
    if (!idx.nameType.has(v.name)) idx.nameType.set(v.name, "view");
  }
  for (const t of model.types) {
    if (t.id) idx.idToName.set(t.id, t.name);
    add(idx.nameCount, t.name);
    if (!idx.nameType.has(t.name)) idx.nameType.set(t.name, "type");
  }
  for (const d of documents) idx.docSlugs.set(d.name, new Set(d.sections.map((s) => s.slug)));
  return idx;
}

function resolveWith(idx: Index, c: ClassifiedRef): RefResolution {
  switch (c.kind) {
    case "id": {
      const name = idx.idToName.get(c.id);
      return { label: name ?? c.id, valid: name !== undefined };
    }
    case "name": {
      const count = idx.nameCount.get(c.name) ?? 0;
      if (count === 1) return { label: c.name, valid: true };
      if (count === 0 && idx.docSlugs.has(c.name)) {
        return { label: c.name, valid: true, href: `archlang://doc/${encodeURIComponent(c.name)}` };
      }
      return { label: c.name, valid: false };
    }
    case "section": {
      const slugs = idx.docSlugs.get(c.doc);
      return { label: `${c.doc} › ${c.section}`, valid: slugs !== undefined && slugs.has(slugify(c.section)) };
    }
    case "aspect": {
      const key = c.value !== undefined ? `${c.key}:${c.value}` : c.key;
      return {
        label: c.value !== undefined ? `${c.key}: ${c.value}` : c.key,
        valid: idx.aspects.has(key),
      };
    }
  }
}

/**
 * Foreign getter `[[ref]]@field` (field) / `[[ref]]@@aspect` (aspect, §4.10 / §5.1):
 * resolve the `ref` element, then read its value on the axis named by `sigil`
 * (a dotted path like `domain` or `security.zone`). Mirrors the core renderer
 * (`renderTextSegment` → `getForeignField` / `getForeignAspect`): only `id` /
 * `name` refs are valid targets, and the pill shows the VALUE.
 *
 *   - valid + value found → label = the value.
 *   - target resolved but value unset → invalid, label = `<sigil>field` (a hint).
 *   - target unresolved / ambiguous   → invalid, label = `ref<sigil>field`.
 */
function resolveForeignWith(idx: Index, c: ClassifiedRef, field: string, sigil: "@" | "@@" = "@"): RefResolution {
  const byId = sigil === "@@" ? idx.aspectsById : idx.fieldsById;
  const byName = sigil === "@@" ? idx.aspectsByName : idx.fieldsByName;
  let values: ValueMap | undefined;
  let targetName: string | undefined;
  if (c.kind === "id") {
    values = byId.get(c.id);
    targetName = idx.idToName.get(c.id);
  } else if (c.kind === "name") {
    const m = byName.get(c.name);
    if (m !== AMBIGUOUS) values = m;
    targetName = c.name;
  }
  const value = values?.get(field);
  if (value !== undefined) return { label: value, valid: true };
  // Target resolvable but no such value → show the getter path as an unresolved
  // hint; otherwise echo the whole getter.
  const hint = values !== undefined || targetName !== undefined ? `${sigil}${field}` : `${defaultLabelInner(c)}${sigil}${field}`;
  return { label: hint, valid: false };
}

/** Inner label for an id/name ref without a model override (getter fallback). */
function defaultLabelInner(c: ClassifiedRef): string {
  if (c.kind === "id") return `#${c.id}`;
  if (c.kind === "name") return c.name;
  return "";
}

export interface MdModelAssist {
  resolveRef: (c: ClassifiedRef, inner: string) => RefResolution;
  /** Resolve a `[[ref]]@field` / `[[ref]]@@aspect` foreign getter to the value on
   *  the axis named by `sigil`. */
  resolveForeignLabel: (c: ClassifiedRef, field: string, sigil?: "@" | "@@") => RefResolution;
  completions: MdCompletion[];
  hover: (inner: string) => MdHoverInfo | undefined;
}

export function buildMdModelAssist(
  model: ArchitectureModel,
  documents: readonly RefResolverDoc[],
): MdModelAssist {
  const idx = collectIndex(model, documents);

  const completions: MdCompletion[] = [];
  for (const [id, name] of idx.idToName) {
    completions.push({ insert: `#${id}`, label: `#${id}`, detail: name, kind: "id" });
  }
  for (const [name, count] of idx.nameCount) {
    if (count !== 1) continue; // only unambiguous names autocomplete cleanly
    completions.push({ insert: name, label: name, detail: idx.nameType.get(name), kind: "name" });
  }
  for (const name of idx.docSlugs.keys()) {
    completions.push({ insert: name, label: name, detail: "document", kind: "doc" });
  }
  for (const a of idx.aspects) {
    completions.push({ insert: `@@${a}`, label: `@@${a}`, detail: "aspect", kind: "aspect" });
  }

  const hover = (inner: string): MdHoverInfo | undefined => {
    if (!inner) return undefined;
    const c = classifyRef(inner);
    const res = resolveWith(idx, c);
    const kindLabel =
      c.kind === "id" ? "element" : c.kind === "section" ? "document section" : c.kind;
    return {
      title: res.label ?? inner,
      detail: res.valid ? kindLabel : `unresolved ${kindLabel}`,
    };
  };

  return {
    resolveRef: (c) => resolveWith(idx, c),
    resolveForeignLabel: (c, field, sigil) => resolveForeignWith(idx, c, field, sigil),
    completions,
    hover,
  };
}

/** Back-compat alias used by the flavored context wiring. */
export function buildRefResolver(
  model: ArchitectureModel,
  documents: readonly RefResolverDoc[],
): (c: ClassifiedRef, inner: string) => RefResolution {
  return buildMdModelAssist(model, documents).resolveRef;
}
